import { ArdoqId } from '@ardoq/api-types';
import { documentArchiveAttachments$ } from 'streams/documentArchive/documentArchiveAttachments$';
import { AttachmentWithContext } from 'streams/documentArchive/types';
import { documentArchiveAttachmentOperations } from 'streams/documentArchive/documentArchiveAttachmentOperations';

const getIsImage = (attachment: AttachmentWithContext) =>
  // oxlint-disable-next-line prefer-string-starts-ends-with
  /^image/.test(attachment['content-type'] || '');

const getDocumentArchiveAttachments = (): AttachmentWithContext[] =>
  documentArchiveAttachments$.state.models;

const getDocumentArchiveImages = ({ wsId }: { wsId: ArdoqId }) =>
  getDocumentArchiveAttachments().filter(
    attachment =>
      getIsImage(attachment) &&
      documentArchiveAttachmentOperations.isOrgOrWorkspaceAttachment(
        attachment,
        wsId
      )
  );

const getWorkspacesAndOrgAttachments = (workspaceIds: Set<ArdoqId>) =>
  getDocumentArchiveAttachments().filter(
    attachment =>
      documentArchiveAttachmentOperations.isOrgAttachment(attachment) ||
      (attachment.rootWorkspace && workspaceIds.has(attachment.rootWorkspace))
  );

const getWorkspaceAndOrgAttachments = (wsId: ArdoqId) =>
  getDocumentArchiveAttachments().filter(attachment =>
    documentArchiveAttachmentOperations.isOrgOrWorkspaceAttachment(
      attachment,
      wsId
    )
  );

const getAttachmentById = (id: string): AttachmentWithContext | null =>
  documentArchiveAttachmentOperations.findById(
    getDocumentArchiveAttachments(),
    id
  );

const getAttachmentByName = (filename: string): AttachmentWithContext | null =>
  documentArchiveAttachmentOperations.findByFilename(
    getDocumentArchiveAttachments(),
    filename
  );

const getAttachmentsNames = () =>
  getDocumentArchiveAttachments().map(({ filename }) => filename);

/**
 * `documentArchiveInterface` snapshots
 * `documentArchiveAttachments$.state` at call time.
 *
 * The app is stream-based now. Join `documentArchiveAttachments$`
 * (`streams/documentArchive/documentArchiveAttachments$.ts`) -- or
 * `documentArchiveFolders$` -- at a permitted boundary and pass the resolved
 * attachments to the pure `documentArchiveAttachmentOperations`
 * (`streams/documentArchive/documentArchiveAttachmentOperations.ts`), which these
 * members already delegate to.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const documentArchiveInterface = {
  /**
   * @deprecated Join `documentArchiveAttachments$` and read `.models`.
   */
  getDocumentArchiveAttachments,
  /**
   * @deprecated Join `documentArchiveAttachments$` and filter the resolved attachments in a pure helper.
   */
  getDocumentArchiveImages,
  /**
   * @deprecated Join `documentArchiveAttachments$` and read `.byId[id]`.
   */
  getAttachmentById,
  /**
   * @deprecated Join `documentArchiveAttachments$` and find by name over `.models`.
   */
  getAttachmentByName,
  /**
   * @deprecated Join `documentArchiveAttachments$` and filter with `documentArchiveAttachmentOperations.isOrgOrWorkspaceAttachment`.
   */
  getWorkspacesAndOrgAttachments,
  /**
   * @deprecated Join `documentArchiveAttachments$` and filter with `documentArchiveAttachmentOperations.isOrgOrWorkspaceAttachment`.
   */
  getWorkspaceAndOrgAttachments,
  /**
   * @deprecated Join `documentArchiveAttachments$` and map the resolved attachments.
   */
  getAttachmentsNames,
};
